/**
 * Bosnian strings for one person's page, and what the Dnevnik gained with it.
 *
 * A slice of its own rather than more of admin.ts, which the People list is
 * changing in the same round: one file per screen is how two people translate
 * at once without one merge conflict between them. The keys stay flat and
 * global, so `admin.log.*` here are the same family as those in admin.ts.
 *
 * Nothing here says who did something in a gendered verb. "Podnio" is wrong
 * for half the people whose page this is, so the tiles say "Završeni: 3"
 * rather than "Završio je 3", and the server words the timeline the same way.
 */

export const people = {
  // ---- Breadcrumb --------------------------------------------------
  // Shown only until the name arrives; then the crumb is the name.
  'crumb.person': 'Osoba',

  // ---- The page ----------------------------------------------------
  'people.loading': 'Učitavanje osobe…',
  'people.back': 'Korisnici',
  'people.forbidden.title': 'Ovu osobu ne možete otvoriti',
  'people.missing.title': 'Ova osoba ne postoji',
  'people.missing.description': 'Možda je obrisana u međuvremenu. Potražite je na listi korisnika.',
  'people.status.active': 'Aktivan',
  'people.plants': 'Pogoni:',
  'people.lastSignIn': 'Posljednja prijava u sistem',
  'people.method.password': 'lozinkom',
  'people.method.badge': 'QR karticom',
  'people.mes.id': 'KaproMES ID {id}',
  'people.mes.open': 'Otvori integraciju s KaproMES-om',
  'people.mes.unfollowed': 'KaproMES ga više ne šalje',
  'people.mes.unfollowed.explain':
    'KaproMES je {when} prestao slati ovu osobu. Nalog je ostao kakav je bio — deaktivirajte ga ovdje ako je osoba otišla.',

  // ---- Actions -----------------------------------------------------
  'people.action.edit': 'Izmijeni',
  'people.action.activate': 'Aktiviraj',
  'people.action.deactivate': 'Deaktiviraj',
  'people.action.deactivate.self': 'Ne možete deaktivirati sopstveni nalog.',
  'people.action.code': 'Izdaj kod',
  'people.action.badge': 'Kartica',
  'people.action.badge.inactive': 'Kartica se izdaje samo aktivnom nalogu.',
  'people.activate.confirm': 'Aktivirati nalog korisnika „{name}"?',
  'people.activate.body': 'Ponovo se može prijaviti istom lozinkom ili karticom kao prije.',
  'people.deactivate.confirm': 'Deaktivirati nalog korisnika „{name}"?',
  'people.deactivate.body':
    'Odmah se odjavljuje sa svih uređaja i ne može se prijaviti dok nalog ponovo ne aktivirate. Prijave, zadaci i obilasci ostaju.',
  'people.activated': 'Nalog je aktiviran.',
  'people.deactivated': 'Nalog je deaktiviran.',
  'people.save.failed': 'Promjena nije sačuvana. Pokušajte ponovo.',

  // ---- Summary -----------------------------------------------------
  'people.summary': 'Sažetak',
  'people.tile.reports': 'Prijave',
  'people.tile.reports.detail': 'Otvorene: {open}',
  'people.tile.tasks': 'Zadaci',
  'people.tile.tasks.detail': 'Završeni: {done} · kasne: {overdue}',
  'people.tile.walks': '5S obilasci',
  'people.tile.walks.detail': 'Na centrima koje održava: {kept}',
  'people.tile.rating': 'Ocjena',
  'people.tile.rating.detail': '{position}. od {ranked} · {month}',
  'people.tile.rating.unranked': 'Bez mjesta na listi · {month}',
  'people.tile.rating.none': 'Ovog mjeseca još nema ocjene',
  'people.tile.rating.thin': 'Na malo obilazaka — tek okvirno.',
  'people.tile.signIns': 'Prijave u sistem, 30 dana',
  'people.tile.signIns.detail': 'Neuspješne: {failed}',
  'people.tile.scope': 'Broji se samo rad u vašim pogonima.',

  // ---- Timeline ----------------------------------------------------
  'people.activity.title': 'Aktivnost',
  'people.activity.explain':
    '„Nalog" su izmjene naloga ove osobe, ko god da ih je napravio. „Radnje" je ono što je ova osoba sama radila — u administraciji, na prijavama i obrascima. Prijave, zadaci i obilasci su samo iz pogona koje vidite.',
  'people.activity.filter.label': 'Vrsta aktivnosti',
  'people.activity.filter.sve': 'Sve',
  'people.activity.filter.prijava-u-sistem': 'Prijave u sistem',
  'people.activity.filter.prijave': 'Prijave',
  'people.activity.filter.zadaci': 'Zadaci',
  'people.activity.filter.5s': '5S',
  'people.activity.filter.nalog': 'Nalog',
  'people.activity.filter.radnje': 'Radnje',
  'people.activity.empty.title': 'Nema aktivnosti',
  'people.activity.empty.description':
    'Ovdje se pojavljuju prijave u sistem, prijave, zadaci, 5S obilasci i izmjene naloga.',
  'people.activity.empty.filter': 'Ove vrste još nema ništa.',
  'people.activity.shown': 'Prikazano {shown} od {total}',
  'people.activity.loadMore': 'Učitaj još',
  'people.activity.ip': 'IP {ip}',
  'people.activity.ip.explain': 'Adresa s koje je stigla prijava. Vidi je samo glavni administrator.',

  // ---- The Dnevnik -------------------------------------------------
  'admin.log.filter.causer.noMatch': 'Te osobe nema u dnevniku',
  'admin.log.export': 'Izvezi (Excel)',
  'admin.log.export.explain':
    'Isti zapisi kao na ekranu, s istim filterima — najviše 10.000 najnovijih.',
  'admin.log.chips.label': 'Uključeni filteri',
  'admin.log.chip.search': 'Traži: „{value}"',
  'admin.log.chip.causerId': 'Osoba: {value}',
  'admin.log.chip.subjectType': 'Šta: {value}',
  'admin.log.chip.event': 'Izmjena: {value}',
  'admin.log.chip.logName': 'Zapis: {value}',
  'admin.log.chip.from': 'Od {value}',
  'admin.log.chip.to': 'Do {value}',
  'admin.log.chip.remove': 'Ukloni filter: {label}',
} as const

export type PeopleSlice = Record<keyof typeof people, string>
